import { useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Printer, Download } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import DOMPurify from 'dompurify';

interface SkillItem {
  id: string;
  name: string;
  years_experience?: number | null;
}

interface GroupedSkills {
  required_technical: SkillItem[];
  good_to_have_technical: SkillItem[];
  soft: SkillItem[];
}

interface PrintJDDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: {
    title: string;
    description: string;
    requirements: string;
    location?: string;
    city?: string;
    country?: string;
    job_type?: string;
    work_mode?: string;
    salary_min?: number | string;
    salary_max?: number | string;
    salary_currency?: string;
  };
  clientName?: string;
  skills: GroupedSkills;
  companyLogo?: string;
}

const PrintJDDialog = ({
  open,
  onOpenChange,
  job,
  clientName,
  skills,
  companyLogo
}: PrintJDDialogProps) => {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${job.title} - Job Description`,
  });

  const requiredTechnical = skills.required_technical || [];
  const goodToHave = skills.good_to_have_technical || [];
  const softSkills = skills.soft || [];

  const formatJobType = (type?: string) => {
    if (!type) return '';
    return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatWorkMode = (mode?: string) => {
    if (!mode) return '';
    return mode.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Print Job Description</DialogTitle>
          <DialogDescription>
            Preview and print the job description as a branded PDF
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-4">
          <Button onClick={() => handlePrint()} className="flex-1">
            <Printer className="h-4 w-4 mr-2" />
            Print / Save as PDF
          </Button>
        </div>

        {/* Printable Content */}
        <div className="border rounded-lg overflow-hidden">
          <div
            ref={printRef}
            className="bg-white text-black p-8"
            style={{ 
              fontFamily: 'Georgia, serif',
              lineHeight: 1.6
            }}
          >
            {/* Header with branding */}
            <div className="border-b-2 border-primary pb-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  {companyLogo && (
                    <img src={companyLogo} alt="Company Logo" className="h-12 mb-2" />
                  )}
                  <h1 className="text-3xl font-bold text-primary">{job.title}</h1>
                  {clientName && (
                    <p className="text-xl text-muted-foreground mt-1">{clientName}</p>
                  )}
                </div>
              </div>

              {/* Job Meta Info */}
              <div className="flex flex-wrap gap-4 mt-4 text-sm">
                {(job.city || job.country || job.location) && (
                  <span className="flex items-center gap-1">
                    📍 {job.city ? `${job.city}, ${job.country}` : job.location}
                  </span>
                )}
                {job.job_type && (
                  <span className="flex items-center gap-1">
                    💼 {formatJobType(job.job_type)}
                  </span>
                )}
                {job.work_mode && (
                  <span className="flex items-center gap-1">
                    🏢 {formatWorkMode(job.work_mode)}
                  </span>
                )}
                {(job.salary_min || job.salary_max) && (
                  <span className="flex items-center gap-1">
                    💰 {job.salary_currency || 'USD'}{' '}
                    {job.salary_min && job.salary_max
                      ? `${Number(job.salary_min).toLocaleString()} - ${Number(job.salary_max).toLocaleString()}`
                      : job.salary_min
                        ? `From ${Number(job.salary_min).toLocaleString()}`
                        : `Up to ${Number(job.salary_max).toLocaleString()}`
                    }
                  </span>
                )}
              </div>
            </div>

            {/* Description */}
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-primary border-b pb-1">About the Role</h2>
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.description) }}
              />
            </section>

            {/* Requirements */}
            {job.requirements && (
              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3 text-primary border-b pb-1">Requirements</h2>
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.requirements) }}
                />
              </section>
            )}

            {/* Skills */}
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-primary border-b pb-1">Skills</h2>
              
              {requiredTechnical.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-semibold text-sm mb-2 text-muted-foreground">Required Technical Skills</h3>
                  <div className="space-y-1">
                    {requiredTechnical.map(skill => (
                      <div key={skill.id} className="flex items-center gap-2">
                        <span className="font-medium">• {skill.name}</span>
                        {skill.years_experience && skill.years_experience > 0 && (
                          <span className="text-sm text-muted-foreground">
                            ({skill.years_experience}+ years)
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {goodToHave.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-semibold text-sm mb-2 text-muted-foreground">Good to Have</h3>
                  <div className="flex flex-wrap gap-2">
                    {goodToHave.map(skill => (
                      <span key={skill.id} className="text-sm">
                        • {skill.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {softSkills.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm mb-2 text-muted-foreground">Soft Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {softSkills.map(skill => (
                      <span key={skill.id} className="text-sm">
                        • {skill.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Footer */}
            <div className="border-t pt-4 mt-8 text-center text-sm text-muted-foreground">
              <p>For inquiries, please contact our recruitment team</p>
              <p className="text-xs mt-2">Generated on {new Date().toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrintJDDialog;
